import { CalendarClock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { AllTransactionViewFilterInput } from "@/api/models/allTransactionView";
import {
  getAllTransactionsCount,
  getAllTransactionsGraphql,
} from "@/api/services/allTransactionGraphqlService";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { ClickableNumber } from "@/components/ui/ClickableNumber";
import { Card, CardContent } from "@/components/ui/card";
import { HoverUnderlineSpan } from "@/components/ui/HoverUnderlineSpan";
import { useApp } from "@/contexts/AppContext";
import { DEMO_UPCOMING_COUNT, DEMO_UPCOMING_TOTAL } from "@/hooks/useDrillDown";
import { usePermissions } from "@/hooks/usePermissions";
import type { DashboardMetrics } from "@/services/metricsService";

const SUPPLIER_BILLS_FILTER: AllTransactionViewFilterInput = {
  source: { eq: "Purchase Ledger" },
  status: { eq: "Outstanding" },
  transactionType: { eq: "Invoice" },
};

interface UpcomingPaymentsMiniCardProps {
  metrics: DashboardMetrics;
  onNavigate?: (
    page: string,
    data?: { columnFilters?: Record<string, string> },
  ) => void;
}

export default function UpcomingPaymentsMiniCard({
  onNavigate,
}: UpcomingPaymentsMiniCardProps) {
  const { hasPermission } = usePermissions();
  const { mode } = useApp();

  // Live mode state
  const [livePaymentCount, setLivePaymentCount] = useState(0);
  const [livePaymentAmount, setLivePaymentAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedLiveData = useRef(false);

  if (!hasPermission("view_upcoming_payments_mini_card")) return null;

  useEffect(() => {
    if (mode !== "live") {
      hasLoadedLiveData.current = false;
      setLivePaymentCount(0);
      setLivePaymentAmount(0);
      return;
    }

    if (hasLoadedLiveData.current) return;

    const loadLiveData = async () => {
      hasLoadedLiveData.current = true;
      setIsLoading(true);

      try {
        const [count, connection] = await Promise.all([
          getAllTransactionsCount(SUPPLIER_BILLS_FILTER),
          getAllTransactionsGraphql(SUPPLIER_BILLS_FILTER, 1000),
        ]);

        const totalOutstanding = connection.edges.reduce(
          (sum, edge) => sum + (edge.node.outstanding ?? 0),
          0,
        );

        setLivePaymentCount(count);
        setLivePaymentAmount(totalOutstanding);
      } catch (err) {
        console.error("Failed to load upcoming payments:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadLiveData();
  }, [mode]);

  if (!hasPermission("view_upcoming_payments_mini_card")) {
    return null;
  }

  const upcomingPayments =
    mode === "live" ? livePaymentCount : DEMO_UPCOMING_COUNT;
  const upcomingAmount =
    mode === "live" ? livePaymentAmount : DEMO_UPCOMING_TOTAL;

  const navigateToSupplierBills = () => {
    onNavigate?.("transactions", {
      columnFilters: {
        source: "Purchase Ledger",
        status: "Outstanding",
        transactionType: "Invoice",
      },
    });
  };

  // Always warning — upcoming bills are inherently a watchlist item

  return (
    <Card
      className="h-full"
      style={{ borderLeft: "3px solid var(--mc-warning-border)" }}
    >
      <CardContent className="p-4 flex flex-col justify-between h-full">
        {/* Top row — label + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">
              Supplier Bills Due
            </span>
            <InfoTooltip
              title="Supplier Bills Due"
              content="Supplier invoices and bills from your connected data sources that are due for payment in the next 30 days. Does not include recurring scheduled transactions."
            />
          </div>
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "var(--mc-warning-bg)" }}
          >
            <CalendarClock
              className="h-4 w-4"
              style={{ color: "var(--mc-warning-icon)" }}
            />
          </div>
        </div>

        {/* Value + sub-text */}
        <div>
          <div className="text-2xl font-bold text-neutral-900 tracking-tight">
            <HoverUnderlineSpan
              onClick={navigateToSupplierBills}
              title="View upcoming supplier bills"
            >
              {isLoading ? "…" : upcomingPayments}
            </HoverUnderlineSpan>
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span className="text-xs font-semibold text-neutral-800">
              <ClickableNumber
                value={upcomingAmount}
                onClick={navigateToSupplierBills}
                showUnderline={true}
                className="text-xs font-semibold text-neutral-800"
              />
            </span>
            <span className="text-xs font-normal text-neutral-800">
              due in 30 days
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
